/* import libraries */
import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'

/* import components */
import Form from 'react-bootstrap/Form'
import ListGroup from 'react-bootstrap/ListGroup'
import InputGroup from 'react-bootstrap/InputGroup'
import Spinner from './Spinner'
import ChevronRight from './icons/ChevronRight'
import SearchIcon from './icons/SearchIcon'

/* import services */
import tripsApi from '../api/tripsApi'
import articlesApi from '../api/articlesApi'
import { PER_PAGE } from './variables/variables'
import { normalizeServiceType } from '../utils/utils'

const TopicOption = (props) => {
  const { state } = useLocation()
  const [topics, setTopics] = useState(props.topics ? props.topics : [])
  const [trip, setTrip] = useState()
  const [currPage, setCurrPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [debounceTimer, setDebounceTimer] = useState(null)
  const section = state?.topic

  const handleChange = async (event) => {
    const query = event.target?.value?.toLowerCase()
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    setDebounceTimer(setTimeout(() => getSearchSuggestions(query), 500))
  }

  const getSearchSuggestions = async (query) => {
    let result = props.topics.filter((topic) => topic.name.toLowerCase().indexOf(query) > -1)

    if (query) {
      // FETCHING ARTICLES FROM THE API
      const response = await articlesApi.getArticleSuggestionsForSearch(query)
      if (response?.status) {
        result = result.concat(response.data)
      }
    }

    setTopics(result)
  }

  const getLinkStringForTopic = (topic) => {
    // Shows a list of articles. Each topic will link to a specific article
    if ((topic.sectionId && topic.sectionId >= 0) || topic.type === 'article') {
      return `topic/${topic.id}`
    }

    // Link to FWD insurance article
    if (topic.sectionId && topic.sectionId < 0) {
      return topic.insuranceUrl
    }

    // Shows a list of sections. Each topic will link to a list of articles in a section
    return `sections/${topic.id}`
  }

    const getInsuranceUrl = (insuranceUrl) => {
        if (insuranceUrl) {
            return `?insuranceUrl=${insuranceUrl}`
        }

        return ''
    }
    
    const getArticles = async (body) => {
        const { id, serviceType, status, page, tripId } = body;
        try {
            let response = "";
            if (id) {
                response = await articlesApi.getArticlesBySection({perPage: PER_PAGE, page: page, sectionId: id});
            } else if (serviceType && status) {
                response = await articlesApi.getArticlesByServiceType({perPage: PER_PAGE, 
                            page: page, serviceType: serviceType, status: status, tripId: tripId});
            }
            setCurrPage(currPage+1);
            if (response.status) {
                setTopics([...topics, ...response.data.data]);
                if (response.data.data.length < PER_PAGE) {
                  setHasMore(false);
                }
            } else {
              console.error(response.error.msg);
            }
        } catch (error) {
            console.error("Error while fetching articles: " + error.message)
        } finally {
            setLoading(false);
        }
    }

  const getTrip = async (tripId) => {
    try {
      setLoading(true)
      const response = await tripsApi.getTripById(tripId)
      if (response.status) {
        setTrip(response.data.data)
        getArticles({
          serviceType: normalizeServiceType(props.serviceTypeFromUrl),
          status: response.data.data.status,
          page: 1,
          tripId: props.tripIdFromUrl
        })
      } else {
        console.error(response.error.msg)
      }
    } catch (error) {
      console.error('Error while fetching trip: ' + error.message)
    }
  }

  const fetchArticles = (page) => {
    // Retrieve articles by section
    if (section) {
      getArticles({ id: section.id, page: page })
    }
    // Retrieve articles by service type
    else if (props.trip?.serviceType) {
      getArticles({
        serviceType: normalizeServiceType(props.trip.serviceType),
        status: props.isComplete,
        page: page,
        tripId: props.trip.id,
      })
    }
    // From mobile entry point (past trips)
    else if (props.tripIdFromUrl && props.serviceTypeFromUrl) {
      if (trip?.id) {
        getArticles({
          serviceType: normalizeServiceType(props.serviceTypeFromUrl),
          status: trip.status,
          page: page,
          tripId: props.tripIdFromUrl,
        })
      } else {
        getTrip(props.tripIdFromUrl)
      }
    }
  }

  useEffect(() => {
    fetchArticles(1)
  }, [props.isComplete])

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }
    }
  }, [debounceTimer])

  if (loading) return <Spinner />
  return (
    <>
      {props.searchBar ? (
        <Form
          className='d-flex'
          style={{ paddingTop: '15px' }}
          onSubmit={(e) => {
            e.preventDefault()
          }}
        >
          <InputGroup>
            <InputGroup.Text>
              <SearchIcon />
            </InputGroup.Text>
            <Form.Control
              type='search'
              inputMode='search'
              placeholder='What can we help you with?'
              aria-label='Search'
              onChange={(event) => handleChange(event)}
            />
          </InputGroup>
        </Form>
      ) : (
        <></>
      )}
      <ListGroup variant='flush' style={{ paddingTop: '10px' }}>
        <InfiniteScroll
          dataLength={currPage * 20}
          next={() => fetchArticles(currPage)}
          hasMore={props.topics ? false : hasMore}
          loader={<h6>Loading...</h6>}
        >
          {topics?.map((topic) => (
            <Link
              to={getLinkStringForTopic(topic)}
              state={{
                topic: topic,
                trip: props.trip,
                trips: topic.sectionId ? state?.trips : props.trips,
              }}
              style={{ textDecoration: 'none' }}
              key={topic.id}
            >
              <ListGroup variant='flush'>
                <ListGroup.Item
                  variant='flush'
                  className='topicOptions alignTextDiffSize grey'
                  style={{
                    fontFamily:
                      props.fontWeight === 'semibold'
                        ? 'Open Sans Semi-Bold'
                        : 'Open Sans Bold',
                  }}
                >
                  <div
                    style={{
                      marginTop: 'auto',
                      marginBottom: 'auto',
                      paddingRight: '15px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {(props.topics && topic.type === 'section') && (
                      <img
                        src={topic.imagePath}
                        width='40px'
                        height='40px'
                        style={{ marginRight: '10px', padding: "2px", flexShrink: 0 }}
                        alt=''
                      />
                    )}
                    <span>{topic.name}</span>
                  </div>
                  <div style={{ marginTop: 'auto', marginBottom: 'auto' }}>
                    <ChevronRight size='12' fill='#D8D8D8' />
                  </div>
                </ListGroup.Item>
                <ListGroup.Item style={{ padding: '1px' }} />
              </ListGroup>
            </Link>
          ))}
        </InfiniteScroll>
      </ListGroup>
    </>
  )
}

export default TopicOption
